import { Controller } from "@hotwired/stimulus"
import { showAlertModal } from "../lib/custom_confirm"

// Row class → key of the deleteUrls value; the view fills each with its route.
const DELETE_URL_KEYS = {
  'carrier-custom-product-fields': 'customProduct',
  'carrier-link-fields': 'link',
  'carrier-rep-fields': 'rep',
  'carrier-document-fields': 'document'
}
const ID_PLACEHOLDER = '__ID__'

export default class extends Controller {
  static targets = [
    "documentContainer", "linkContainer", "repContainer", "customProductContainer",
    "addDocumentBtn", "addLinkBtn", "addRepBtn", "addCustomProductBtn"
  ]
  // deleteUrls (#19320): the routed destroy URL per row type, with ID_PLACEHOLDER
  // where the row id goes — passed from the view's route helpers so no path is
  // hardcoded here (the retired paths it once hardcoded broke every Remove).
  static values = {
    deleteUrls: Object,
    documentIndex: Number,
    linkIndex: Number,
    repIndex: Number,
    customProductIndex: Number
  }

  connect() {
    // Initialize indices based on existing fields
    this.documentIndexValue = this.element.querySelectorAll('.carrier-document-fields').length
    this.linkIndexValue = this.element.querySelectorAll('.carrier-link-fields').length
    this.repIndexValue = this.element.querySelectorAll('.carrier-rep-fields').length
    this.customProductIndexValue = this.element.querySelectorAll('.carrier-custom-product-fields').length
  }

  addDocument() {
    const newField = this.buildDocumentField(this.documentIndexValue)
    this.documentContainerTarget.insertAdjacentHTML('beforeend', newField)
    this.documentIndexValue++
  }

  addLink() {
    const newField = this.buildLinkField(this.linkIndexValue)
    this.linkContainerTarget.insertAdjacentHTML('beforeend', newField)
    this.linkIndexValue++
  }

  addRep() {
    const newField = this.buildRepField(this.repIndexValue)
    this.repContainerTarget.insertAdjacentHTML('beforeend', newField)
    this.repIndexValue++
  }

  addCustomProduct() {
    const newField = this.buildCustomProductField(this.customProductIndexValue)
    this.customProductContainerTarget.insertAdjacentHTML('beforeend', newField)
    this.customProductIndexValue++
  }

  buildDocumentField(index) {
    return `
      <div class="carrier-document-fields border rounded p-3 mb-3 bg-body-tertiary">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <strong>New Document</strong>
        </div>
        <div class="row">
          <div class="col-md-6 mb-2">
            <label class="form-label">Document Title</label>
            <input type="text" name="carrier[carrier_documents_attributes][${index}][title]" class="gl-form-control" required>
          </div>
          <div class="col-md-6 mb-2">
            <label class="form-label">Category</label>
            <select name="carrier[carrier_documents_attributes][${index}][category]" class="gl-form-control" required>
              <option value="compliance">Compliance</option>
              <option value="marketing">Marketing</option>
              <option value="training">Training</option>
              <option value="compensation">Compensation</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <div class="mb-2">
          <label class="form-label">Description</label>
          <textarea name="carrier[carrier_documents_attributes][${index}][description]" class="gl-form-control" rows="2"></textarea>
        </div>
        <div class="mb-2">
          <label class="form-label">Upload Document</label>
          ${this.fileDropSlotHTML({
            name: `carrier[carrier_documents_attributes][${index}][document]`,
            accept: "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            required: true,
            hint: "Accepted: PDF, Word, Excel files (max 10MB)"
          })}
        </div>
      </div>
    `
  }

  buildLinkField(index) {
    return `
      <div class="carrier-link-fields border rounded p-3 mb-3 bg-body-tertiary">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <strong>Link</strong>
          <div class="d-flex align-items-center gap-2">
            <button type="button" class="gl-btn gl-btn-outline-danger gl-btn-sm" data-action="carrier-form#removeNewLink">
              <i class="ph ph-trash me-1"></i>
              Remove
            </button>
            <button type="submit" class="gl-btn gl-btn-primary gl-btn-sm" data-card-dirty-target="submit">
              <i class="ph ph-check me-1"></i>
              Save
            </button>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6 mb-2">
            <label class="form-label">Link Title</label>
            <input type="text" name="carrier[carrier_links_attributes][${index}][title]" class="gl-form-control" required>
          </div>
          <div class="col-md-6 mb-2">
            <label class="form-label">Category</label>
            <select name="carrier[carrier_links_attributes][${index}][category]" class="gl-form-control" required>
              <option value="portal">Portal</option>
              <option value="training">Training</option>
              <option value="resources">Resources</option>
              <option value="support">Support</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <div class="mb-2">
          <label class="form-label">URL</label>
          <input type="url" name="carrier[carrier_links_attributes][${index}][url]" class="gl-form-control" placeholder="https://example.com" required>
        </div>
        <div class="mb-2">
          <label class="form-label">Description</label>
          <textarea name="carrier[carrier_links_attributes][${index}][description]" class="gl-form-control" rows="2"></textarea>
        </div>
      </div>
    `
  }

  buildRepField(index) {
    return `
      <div class="carrier-rep-fields border rounded p-3 mb-3 bg-body-tertiary">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <strong>New Representative</strong>
        </div>
        <div class="row">
          <div class="col-md-4 mb-2">
            <label class="form-label">Name</label>
            <input type="text" name="carrier[carrier_reps_attributes][${index}][name]" class="gl-form-control" required>
          </div>
          <div class="col-md-4 mb-2">
            <label class="form-label">Cell Phone</label>
            <input type="tel" name="carrier[carrier_reps_attributes][${index}][cell_phone]" class="gl-form-control" data-controller="phone-format" data-action="input->phone-format#format">
          </div>
          <div class="col-md-4 mb-2">
            <label class="form-label">Email</label>
            <input type="email" name="carrier[carrier_reps_attributes][${index}][email]" class="gl-form-control" required>
          </div>
        </div>
        <input type="hidden" name="carrier[carrier_reps_attributes][${index}][position]" value="${index}">
      </div>
    `
  }

  buildCustomProductField(index) {
    return `
      <div class="carrier-custom-product-fields border rounded p-3 mb-3 bg-body-tertiary">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <strong>Product</strong>
          <div class="d-flex align-items-center gap-2">
            <button type="button" class="gl-btn gl-btn-outline-danger gl-btn-sm" data-action="carrier-form#removeNewProduct">
              <i class="ph ph-trash me-1"></i>
              Remove
            </button>
            <button type="submit" class="gl-btn gl-btn-primary gl-btn-sm" data-card-dirty-target="submit">
              <i class="ph ph-check me-1"></i>
              Save
            </button>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6 mb-2">
            <label class="form-label">Product Name</label>
            <input type="text" name="carrier[carrier_custom_products_attributes][${index}][name]" class="gl-form-control" required data-controller="proper-case" data-action="blur->proper-case#format">
          </div>
          <div class="col-md-6 mb-2">
            <label class="form-label">PDF Document (Optional)</label>
            ${this.fileDropSlotHTML({
              name: `carrier[carrier_custom_products_attributes][${index}][document]`,
              accept: "application/pdf",
              hint: "Accepted: PDF files only (max 10MB)"
            })}
          </div>
        </div>
        <input type="hidden" name="carrier[carrier_custom_products_attributes][${index}][position]" value="${index}">
      </div>
    `
  }

  // Client-built rows render the same shared file-drop slot as the
  // server-rendered rows (shared/_file_drop_slot.html.haml — audit F59
  // tier 2). Keep this markup in sync with the partial.
  fileDropSlotHTML({ name, accept, required = false, hint = null }) {
    return `
      <div class="gl-file-drop-slot" data-controller="file-drop" data-action="dragenter->file-drop#dragEnter dragover->file-drop#dragOver dragleave->file-drop#dragLeave drop->file-drop#drop">
        <div class="gl-file-dropzone gl-file-dropzone--compact" data-file-drop-target="zone">
          <i class="ph ph-cloud-arrow-up gl-file-dropzone-icon"></i>
          <div class="gl-file-dropzone-prompt">Drop your file here</div>
          <div class="gl-file-dropzone-hint">or click to browse from your computer</div>
          <input type="file" name="${name}" accept="${accept}" class="gl-file-drop-input" data-file-drop-target="input" data-action="change->file-drop#filesChanged"${required ? " required" : ""}>
        </div>
        <div class="gl-file-drop-slot__chips" data-file-drop-target="chips"></div>
        ${hint ? `<div class="form-text small">${hint}</div>` : ""}
      </div>
    `
  }

  removeNewProduct(event) {
    event?.preventDefault()
    const productField = event.target.closest('.carrier-custom-product-fields')
    if (productField) {
      productField.remove()
    }
  }

  removeNewLink(event) {
    event?.preventDefault()
    const linkField = event.target.closest('.carrier-link-fields')
    if (linkField) {
      linkField.remove()
    }
  }

  // The destroy URL for a persisted row, or undefined for an unknown row type.
  deleteUrlFor(field, itemId) {
    const type = Object.keys(DELETE_URL_KEYS).find((cls) => field.classList.contains(cls))
    const template = type && this.deleteUrlsValue[DELETE_URL_KEYS[type]]
    return template ? template.replace(ID_PLACEHOLDER, encodeURIComponent(itemId)) : undefined
  }

  async deleteItem(event) {
    event?.preventDefault()

    const button = event.target.closest('button')
    const field = event.target.closest('.carrier-custom-product-fields') ||
                  event.target.closest('.carrier-link-fields') ||
                  event.target.closest('.carrier-rep-fields') ||
                  event.target.closest('.carrier-document-fields')

    if (!field) {
      console.error('[carrier-form] deleteItem: Could not find parent field element')
      return
    }

    const itemId = field.dataset.id
    if (!itemId) {
      // New item without ID, just remove from DOM
      field.remove()
      return
    }

    const deleteUrl = this.deleteUrlFor(field, itemId)

    if (!deleteUrl) {
      console.error('[carrier-form] deleteItem: Unknown field type:', field.classList.toString())
      showAlertModal('Unable to delete this item type.', { variant: 'danger' })
      return
    }

    // Disable button and show loading state
    button.disabled = true
    button.innerHTML = '<i class="ph ph-hourglass me-1"></i> Removing...'

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (!csrfToken) {
        throw new Error('CSRF token not found')
      }

      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        field.remove()
      } else {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Delete failed')
      }
    } catch (error) {
      // Restore button on error
      button.disabled = false
      button.innerHTML = '<i class="ph ph-trash me-1"></i> Remove'
      showAlertModal(`Failed to delete item: ${error.message}`, { variant: 'danger' })
    }
  }
}
